import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Terminal, Building2, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useBrand } from "@/config/brands";

interface SearchBarProps {
  onSearch?: (query: string, type: 'hospital' | 'procedure') => void;
  placeholder?: string;
  autoFocus?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  initialSearchType?: 'hospital' | 'procedure';
}

interface AutocompleteItem {
  id: string;
  name: string;
  city: string;
  state: string;
  type: 'hospital';
  totalProcedures: number;
  priceMarkupPercentage: number;
  shameScore: number;
}


export function SearchBarWithAutocomplete({ 
  onSearch, 
  placeholder = "Search hospitals, procedures, or medical codes...",
  autoFocus = false,
  value,
  onChange,
  initialSearchType = 'hospital'
}: SearchBarProps) {
  const [internalQuery, setInternalQuery] = useState(value || "");
  const query = value !== undefined ? value : internalQuery;
  const setQuery = onChange || setInternalQuery;
  const [searchType, setSearchType] = useState<'hospital' | 'procedure'>(initialSearchType);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  // Update search type when initialSearchType changes
  useEffect(() => {
    setSearchType(initialSearchType);
  }, [initialSearchType]);
  const [isLoading, setIsLoading] = useState(false);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Query hospital data for autocomplete using the same data source as rankings page
  const { data: hospitalResults = [], isLoading: isLoadingHospitals } = useQuery({
    queryKey: ['hospital-autocomplete', query],
    queryFn: async () => {
      if (!query.trim() || query.length < 2 || searchType !== 'hospital') return [];
      
      // Use a join approach to get hospitals with rankings that match the search.
      // Tokenize the query on whitespace and AND the tokens (each matched across
      // name/city/state) so "Texas Health Kaufman" resolves "Texas Health
      // Presbyterian Hospital Kaufman" — chained .or() calls AND together in
      // PostgREST, so every token must appear in one of the three fields.
      let hospitalQuery = supabase
        .from('hospitals')
        .select(`
          id, name, city, state, cpt_count, drg_count, revenue_count, supply_count, icd10_count,
          hospital_ranking_summary!inner(
            avg_gross_markup_percentage,
            median_gross_markup_percentage,
            shame_score_calculated,
            total_procedures
          )
        `);
      for (const token of query.trim().split(/\s+/)) {
        const t = token.replace(/[%,()]/g, ''); // strip PostgREST filter metachars (comma/paren break .or() parsing)
        if (t) hospitalQuery = hospitalQuery.or(`name.ilike.%${t}%,city.ilike.%${t}%,state.ilike.%${t}%`);
      }
      const { data: joinedData, error: joinedError } = await hospitalQuery
        .not('hospital_ranking_summary.avg_gross_markup_percentage', 'is', null)
        .limit(10);

      if (joinedError) throw joinedError;
      if (!joinedData) return [];

      // Transform the joined data
      const hospitalsData = joinedData.map(item => ({
        id: item.id,
        name: item.name,
        city: item.city,
        state: item.state,
        cpt_count: item.cpt_count,
        drg_count: item.drg_count,
        revenue_count: item.revenue_count,
        supply_count: item.supply_count,
        icd10_count: item.icd10_count,
        ranking: item.hospital_ranking_summary[0] // Get the first (and only) ranking record
      }));

      // Transform to the expected format
      return hospitalsData.map(hospital => {
        const totalProcedures = (hospital.cpt_count || 0) + (hospital.drg_count || 0) + (hospital.revenue_count || 0) + (hospital.supply_count || 0) + (hospital.icd10_count || 0);

        return {
          id: hospital.id,
          name: hospital.name,
          city: hospital.city,
          state: hospital.state,
          type: 'hospital' as const,
          totalProcedures,
          priceMarkupPercentage: hospital.ranking?.median_gross_markup_percentage ?? hospital.ranking?.avg_gross_markup_percentage ?? 0,
          shameScore: hospital.ranking?.shame_score_calculated || 0,
        };
      });
    },
    enabled: query.length >= 2 && searchType === 'hospital',
    staleTime: 30000, // Cache for 30 seconds
  });




  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || hospitalResults.length === 0) return;
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % hospitalResults.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev <= 0 ? hospitalResults.length - 1 : prev - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && hospitalResults[selectedIndex]) {
          handleSelectHospital(hospitalResults[selectedIndex]);
        } else {
          handleSubmit(e);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleSelectHospital = (hospital: AutocompleteItem) => {
    setQuery(hospital.name);
    setShowDropdown(false);
    setSelectedIndex(-1);
    navigate(`/hospital/${hospital.id}`);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      setIsLoading(true);
      setShowDropdown(false);
      if (onSearch) {
        onSearch(query.trim(), searchType);
      } else {
        // Navigate to search page
        navigate(`/search?q=${encodeURIComponent(query.trim())}&type=${searchType}`);
      }
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  // Show/hide dropdown based on focus and results
  useEffect(() => {
    if (query.length >= 2 && searchType === 'hospital' && hospitalResults.length > 0) {
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
      setSelectedIndex(-1);
    }
  }, [hospitalResults, query, searchType]);

  // Handle clicking outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Color coding function for markup percentages
  const getMarkupColor = (markup: number): string => {
    if (markup <= -10) return "text-green-600";
    if (markup < 10) return "text-gray-600";
    if (markup < 50) return "text-orange-500";
    return "text-red-600";
  };

  const brand = useBrand();
  const { t } = useTranslation(['common', 'search']);

  // Brand-specific styles with mobile responsiveness
  const inputClasses = brand.key === 'antigreed'
    ? "pl-10 pr-20 py-4 sm:pl-12 sm:pr-32 sm:py-6 text-base sm:text-lg font-mono bg-white/80 border-2 border-emerald-700/40 text-gray-900 placeholder:text-gray-500 focus:border-emerald-700 focus:ring-2 focus:ring-emerald-600/20 transition-all duration-300 group-hover:border-emerald-600/60"
    : "pl-10 pr-20 py-4 sm:pl-12 sm:pr-32 sm:py-6 text-base sm:text-lg font-mono bg-secondary border-border focus:border-primary focus:shadow-matrix transition-all duration-300 group-hover:border-primary/50";

  const buttonClasses = brand.key === 'antigreed'
    ? "absolute right-1 sm:right-2 top-1/2 transform -translate-y-1/2 px-3 py-2 sm:px-6 sm:py-2 bg-emerald-700 hover:bg-emerald-800 text-white font-bold transition-all duration-300 shadow-md hover:shadow-lg text-xs sm:text-sm"
    : "absolute right-1 sm:right-2 top-1/2 transform -translate-y-1/2 px-3 py-2 sm:px-6 sm:py-2 bg-primary hover:bg-primary/90 text-primary-foreground font-bold transition-all duration-300 hover:shadow-matrix text-xs sm:text-sm";

  const iconClasses = brand.key === 'antigreed'
    ? "absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-emerald-700 z-10"
    : "absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-primary z-10";

  const dropdownClasses = brand.key === 'antigreed'
    ? "absolute top-full left-0 right-0 mt-1 bg-white border-2 border-emerald-700/40 rounded-md shadow-xl z-[9999] max-h-80 overflow-y-auto"
    : "absolute top-full left-0 right-0 mt-1 bg-secondary border border-border rounded-md shadow-lg z-[9999] max-h-80 overflow-y-auto";

  const dropdownItemClasses = (index: number, selectedIndex: number) => {
    const isSelected = index === selectedIndex;
    if (brand.key === 'antigreed') {
      return `px-4 py-3 cursor-pointer transition-colors ${
        isSelected
          ? 'bg-emerald-50 border-l-4 border-emerald-700'
          : 'hover:bg-amber-50'
      }`;
    }
    return `px-4 py-3 cursor-pointer transition-colors ${
      isSelected
        ? 'bg-primary/20 border-l-4 border-primary'
        : 'hover:bg-muted'
    }`;
  };

  const dropdownIconClasses = brand.key === 'antigreed'
    ? "w-4 h-4 text-emerald-700 shrink-0"
    : "w-4 h-4 text-primary shrink-0";

  const dropdownTextClasses = brand.key === 'antigreed'
    ? "font-semibold text-gray-900 truncate"
    : "font-semibold text-foreground truncate";

  const dropdownSubtextClasses = brand.key === 'antigreed'
    ? "text-sm text-gray-600"
    : "text-sm text-muted-foreground";

  return (
    <div className="w-full max-w-4xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="relative group">
          <div className={iconClasses}>
            <Terminal className="w-5 h-5" />
          </div>
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (onChange) onChange(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            autoFocus={autoFocus}
            className={inputClasses}
          />
          <Button
            type="submit"
            disabled={isLoading}
            className={buttonClasses}
          >
            <Search className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">
              {isLoading ? t('common:searching') : t('common:actions.search')}
            </span>
          </Button>

          {/* Autocomplete Dropdown */}
          {showDropdown && hospitalResults.length > 0 && (
            <div
              ref={dropdownRef}
              className={dropdownClasses}
            >
              {hospitalResults.map((hospital, index) => (
                <div
                  key={hospital.id}
                  className={dropdownItemClasses(index, selectedIndex)}
                  onClick={() => handleSelectHospital(hospital)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Building2 className={dropdownIconClasses} />
                        <span className={dropdownTextClasses}>
                          {hospital.name}
                        </span>
                      </div>
                      <div className={`flex items-center gap-1 ${dropdownSubtextClasses}`}>
                        <MapPin className="w-3 h-3 shrink-0" />
                        <span>{hospital.city}, {hospital.state}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 ml-4 shrink-0">
                      {/* Price Markup */}
                      <div className="text-right">
                        <div className={`font-mono font-bold text-sm ${getMarkupColor(hospital.priceMarkupPercentage || 0)}`}>
                          {(hospital.priceMarkupPercentage || 0) >= 0 ? '+' : ''}{(hospital.priceMarkupPercentage || 0).toFixed(1)}%
                        </div>
                        <div className="text-xs text-muted-foreground">{t('search:autocomplete.markup')}</div>
                      </div>
                      
                      {/* Shame Score */}
                      <div className="text-right">
                        <div className="font-mono font-bold text-sm text-red-500">
                          {(hospital.shameScore || 0).toFixed(0)}
                        </div>
                        <div className="text-xs text-muted-foreground">{t('search:autocomplete.shame')}</div>
                      </div>
                      
                      {/* Total Procedures */}
                      <div className="text-right">
                        <div className="font-mono font-bold text-sm text-foreground">
                          {(hospital.totalProcedures || 0).toLocaleString()}
                        </div>
                        <div className="text-xs text-muted-foreground">{t('search:autocomplete.procs')}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </form>
    </div>
  );
}